import { useEffect, useRef, useState } from "react";
import { Coa, ExternalCoa } from "../types";
import {
    ExternalChartOfAccountsMappingPartialMappingInput,
    useUpdateChartOfAccountsMappingPartialMutation
} from "../../../../__generated__/generated_types";
import { toast } from "react-toastify";

export interface IUseMappingUpdater {
    partialUpdate: (coaId: string, externalId: string[]) => void;
    enableAutoSave: (enabled: boolean) => void;
    autoSave: boolean;
    saving: boolean;
    lastSaved: Date | null;
    save: () => void;
}

export interface UseMappingUpdaterProps {
    coa: Coa;
    externalCoa: ExternalCoa;
}

export function useMappingUpdater(props: UseMappingUpdaterProps): IUseMappingUpdater {
    const [updateMapping, {loading: updateLoading}] = useUpdateChartOfAccountsMappingPartialMutation({
        notifyOnNetworkStatusChange: true
    });
    const pendingUpdates = useRef<Map<string, string[]>>(new Map());
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [beingSaved, setBeingSaved] = useState<Map<string, string[]> | null>(null);
    const [autoSave, setAutoSave] = useState<boolean>(true);

    useEffect(() => {
        if(!autoSave) {
            return;
        }

        const interval = setInterval(() => {
            save();
        }, 30000);
        return () => clearInterval(interval);
    }, [autoSave]);

    useEffect(() => {
        if(!beingSaved) {
            return;
        }

        updateMapping({
            variables: {
                coaId: props.coa.id,
                externalCoaId: props.externalCoa.id,
                partialMapping: Array.from(beingSaved.entries()).map(entry => ({
                    accountId: entry[0],
                    externalAccountIds: entry[1]
                }))
            }
        }).then(res => {
            if(res.data?.externalChartOfAccountsMappingPartialUpdate) {
                //toast.success("Saved Mapping.");
                setLastSaved(new Date());
            }
        }).catch(() => {
            toast.error("Unable to Saving Mapping!");
            saveFailed(beingSaved);
        }).finally(() => {
            setBeingSaved(null);
        });
    }, [beingSaved, props.coa, props.externalCoa]);

    const saveFailed = (wasBeingSaved: Map<string, string[]>): void => {
        const currentPending = pendingUpdates.current;
        wasBeingSaved.forEach((externalIds, coaId) => {
            if(!currentPending.has(coaId)) {
                currentPending.set(coaId, externalIds);
            }
        });
    };

    const partialUpdate = (coaId: string, externalId: string[]) => {
        pendingUpdates.current.set(coaId, externalId);
    };

    const enableAutoSave = (enabled: boolean) => {
        setAutoSave(enabled);
    };

    const save = (doSetLastSaved?: boolean) => {
        // Check we aren't already in the middle of a save
        // to prevent issues with "dual saves".
        if(beingSaved !== null) {
            return;
        }

        const toSave = pendingUpdates.current;
        if(toSave.size > 0){
            pendingUpdates.current = new Map();
            setBeingSaved(toSave);
        } else if(doSetLastSaved === true) {
            setLastSaved(new Date());
        }
    };

    return {
        partialUpdate: partialUpdate,
        enableAutoSave: enableAutoSave,
        autoSave: autoSave,
        lastSaved: lastSaved,
        saving: beingSaved !== null || updateLoading,
        save: () => save(true)
    };
}